import axios, { AxiosRequestConfig, AxiosResponse } from 'axios';
import { getApiConfig, buildApiUrl, ApiResponse, apiConfigs } from './config';

// Contact form data interface
export interface ContactFormData {
  name: string;
  email: string;
  message: string;
  phone?: string;
  subject?: string;
  company?: string;
  services?: string[];
  description?: string;
}

  // API service class for dynamic API calls
class ApiService {
  private config = getApiConfig();
  private hasTriedFallback = false;

  // Generic API call method with retry logic
  private async makeRequest<T = any>(
    endpoint: string,
    data: any,
    options: Partial<AxiosRequestConfig> = {}
  ): Promise<ApiResponse<T>> {
    let url = `${this.config.baseUrl}${endpoint}`;
    
    const requestConfig: AxiosRequestConfig = {
      method: 'POST',
      url,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...options.headers,
      },
      data,
      timeout: this.config.timeout,
      ...options,
    };

    let lastError: any;
    
    // Retry logic
    for (let attempt = 1; attempt <= this.config.retryAttempts; attempt++) {
      try {
        console.log(`API Request (Attempt ${attempt}/${this.config.retryAttempts}):`, {
          url,
          data: { ...data, message: data.message?.substring(0, 100) + '...' }, // Log truncated message
        });

        const response: AxiosResponse<ApiResponse<T>> = await axios(requestConfig);
        
        console.log(`API Response (Attempt ${attempt}):`, response.data);
        return response.data;
        
      } catch (error: any) {
        lastError = error;
        
        // If localhost connection fails and we haven't tried fallback, switch to production API
        if (
          !this.hasTriedFallback &&
          (error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED') &&
          (error.message?.includes('CONNECTION_REFUSED') || error.message?.includes('Network Error')) &&
          url.includes('localhost')
        ) {
          console.warn('⚠️ Localhost API not available, switching to production API...');
          this.hasTriedFallback = true;
          localStorage.setItem('useProductionAPI', 'true');
          const productionConfig = apiConfigs.production;
          url = `${productionConfig.baseUrl}${endpoint}`;
          requestConfig.url = url;
          requestConfig.timeout = productionConfig.timeout;
          this.config = productionConfig; // Update config
          continue; // Retry with production API
        }
        
        console.error(`API Error (Attempt ${attempt}/${this.config.retryAttempts}):`, {
          message: error.message,
          status: error.response?.status,
          data: error.response?.data,
        });

        // Don't retry on client errors (4xx) except 408, 429
        if (error.response?.status >= 400 && error.response?.status < 500 && 
            ![408, 429].includes(error.response.status)) {
          break;
        }

        // Wait before retry (exponential backoff)
        if (attempt < this.config.retryAttempts) {
          const delay = Math.pow(2, attempt) * 1000; // 2s, 4s, 8s...
          console.log(`Retrying in ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }

    // All retries failed
    throw this.handleError(lastError);
  }

  // Error handling
  private handleError(error: any): Error {
    if (error.response) {
      // Server responded with error status
      const status = error.response.status;
      const data = error.response.data;
      
      switch (status) {
        case 400:
          return new Error(data?.error || data?.message || 'Invalid request data');
        case 401:
          return new Error('Unauthorized access');
        case 403:
          return new Error('Access forbidden');
        case 404:
          return new Error('API endpoint not found');
        case 429:
          return new Error('Too many requests. Please try again later');
        case 500:
          return new Error('Server error. Please try again later');
        default:
          return new Error(data?.error || data?.message || `Server error (${status})`);
      }
    } else if (error.request) {
      // Network error
      return new Error('Network error. Please check your internet connection');
    } else {
      // Other error
      return new Error(error.message || 'An unexpected error occurred');
    }
  }

  // Contact form submission
  async sendContactForm(data: ContactFormData): Promise<ApiResponse> {
    return this.makeRequest('/contact/submit.php', data);
  }

  // Generic method for other API calls
  async post<T = any>(endpoint: string, data: any, options?: Partial<AxiosRequestConfig>): Promise<ApiResponse<T>> {
    return this.makeRequest(endpoint, data, options);
  }

  // Update configuration (useful for runtime changes)
  updateConfig() {
    this.config = getApiConfig();
  }
}

// Create singleton instance
const apiService = new ApiService();

// Export functions for backward compatibility and convenience
export const sendMail = async (data: ContactFormData): Promise<ApiResponse> => {
  return apiService.sendContactForm(data);
};

// Export the service instance for advanced usage
export { apiService };

// Export types
export type { ApiResponse };

